import { isDefined } from "@clipboard-health/util-ts";
import { Box, Chip, type ChipProps, type SxProps, type Theme, useTheme } from "@mui/material";

import { mergeSxProps } from "../../lib/mergeSxProps";
import { CbhIcon, type IconType } from "../CbhIcon";
import { Text } from "../Text";

export const PILL_VARIANTS = ["filled", "outlined", "outlined-dark", "icon-highlight"] as const;
export const PILL_SIZES = ["small", "medium"] as const;

export type PillVariant = (typeof PILL_VARIANTS)[number];
export type PillSize = (typeof PILL_SIZES)[number];

type ColorFunction = (theme: Theme) => string | undefined;

export interface BasePillProps {
  sx?: SxProps<Theme>;
  disabled?: boolean;
  size?: PillSize;
  backgroundColor?: ColorFunction;
  color?: ColorFunction;
  iconColor?: ColorFunction;
  outlineColor?: ColorFunction;
  variant?: PillVariant;
  multiline?: boolean;
}

export interface PillWithLabel extends BasePillProps {
  label: React.ReactNode;
  iconType?: IconType;
}

export interface PillWithIcon extends BasePillProps {
  label?: React.ReactNode;
  iconType: IconType;
}

export interface IconHighlightPill extends BasePillProps {
  label?: React.ReactNode;
  iconType: IconType;
  variant: "icon-highlight";
  backgroundColor: ColorFunction;
  iconColor: ColorFunction;
  outlineColor: never;
}

export type PillProps = PillWithLabel | PillWithIcon | IconHighlightPill;

const pillVariantToChipVariant: Record<PillVariant, ChipProps["variant"]> = {
  filled: "filled",
  outlined: "outlined",
  "outlined-dark": "outlined",
  "icon-highlight": "filled",
};

const getStyleForVariant = (
  theme: Theme,
  variant: PillVariant,
  backgroundColor?: ColorFunction,
  outlineColor?: ColorFunction
) => {
  if (variant === "outlined-dark") {
    return {
      borderColor: outlineColor?.(theme) ?? theme.palette.border?.main,
      backgroundColor: backgroundColor?.(theme),
    };
  }

  if (variant === "outlined") {
    return {
      borderColor: outlineColor?.(theme) ?? theme.palette.border?.primary,
      backgroundColor: backgroundColor?.(theme),
    };
  }

  if (variant === "icon-highlight") {
    return { backgroundColor: theme.palette.background.tertiary };
  }

  if (!isDefined(backgroundColor)) {
    return {};
  }

  return { backgroundColor: backgroundColor(theme) };
};

interface PillIconProps {
  iconType: IconType;
  variant: PillVariant;
  backgroundColor: string;
  iconColor: string;
  size: PillSize;
  disabled?: boolean;
  hasLabel?: boolean;
}

/**
 * This is a helper component to render the icon for the Pill component.
 * It is intentionally not exported from this file
 */
function PillIcon(props: PillIconProps): React.ReactNode {
  const { iconType, variant, backgroundColor, size, iconColor, disabled, hasLabel } = props;

  if (variant === "icon-highlight") {
    return (
      <Box
        sx={(theme) => ({
          padding: 1,
          backgroundColor: disabled ? theme.palette.background.tertiary : backgroundColor,
          color: disabled ? theme.palette.intent?.disabled.icon : iconColor,
          borderRadius: "50%",
          flexShrink: 0,
          display: "flex",
          // for this variant, medium size pill icon should be moved a little bit to the left
          marginLeft: size === "medium" && hasLabel ? `calc(-1 * ${theme.spacing(2)})` : undefined,
        })}
      >
        <CbhIcon type={iconType} size="xSmall" />
      </Box>
    );
  }

  return (
    <CbhIcon
      type={iconType}
      size="small"
      sx={(theme) => ({
        color: disabled ? theme.palette.intent?.disabled.icon : iconColor,
      })}
    />
  );
}

export function Pill(props: PillProps) {
  const {
    backgroundColor,
    disabled,
    label,
    color,
    size = "medium",
    sx,
    variant = "filled",
    iconType,
    iconColor,
    outlineColor,
    multiline = false,
  } = props;

  const theme = useTheme();

  return (
    <Chip
      disabled={disabled}
      label={
        typeof label === "string" ? (
          <Text semibold variant={size === "small" ? "caption" : "body2"}>
            {label}
          </Text>
        ) : (
          label
        )
      }
      icon={
        isDefined(iconType) ? (
          <PillIcon
            backgroundColor={backgroundColor?.(theme) ?? theme.palette.background.primary}
            iconColor={iconColor?.(theme) ?? theme.palette.text.primary}
            iconType={iconType}
            variant={variant}
            size={size}
            disabled={disabled}
            hasLabel={isDefined(label)}
          />
        ) : undefined
      }
      sx={mergeSxProps(
        (theme) => ({
          ...getStyleForVariant(theme, variant, backgroundColor, outlineColor),

          "& .MuiChip-label, & .MuiTypography-root": {
            color: color?.(theme),
          },

          "& .MuiChip-icon": {
            color: iconColor?.(theme),
          },

          // Styles to enable wrapping of label text
          // Check: https://mui.com/material-ui/react-chip/#multiline-chip
          ...(multiline && {
            height: "auto",
            "& .MuiChip-label": {
              display: "block",
              whiteSpace: "normal",
            },
          }),
        }),
        sx
      )}
      variant={pillVariantToChipVariant[variant]}
      size={size}
    />
  );
}
